import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import FolkSaptapurHeader from './FolkSaptapurHeader';
import FolkSaptapurTabs from './FolkSaptapurTabs';
import { folkSaptapurAPI } from '../../services/api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_STYLE = {
  P: { bgcolor: 'success.light', color: 'success.dark' },
  A: { bgcolor: 'error.light', color: 'error.dark' },
  N: { bgcolor: 'info.light', color: 'info.dark' },
  NC: { bgcolor: 'warning.light', color: 'warning.dark' },
  U: { bgcolor: 'action.hover', color: 'text.secondary' },
};

function SadhanaReport() {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState(31);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [includeNative, setIncludeNative] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await folkSaptapurAPI.getReport(month, year);
      setData(response.data.data);
      setDaysInMonth(response.data.days_in_month);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const handleExport = async () => {
    try {
      const response = await folkSaptapurAPI.exportReport(month, year, includeNative);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Sadhana_Report_${MONTHS[month - 1]}_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export report');
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <Box>
      <FolkSaptapurHeader />
      <FolkSaptapurTabs />
      <Container maxWidth={false} sx={{ mt: 3, mb: 4, px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6">Sadhana Report</Typography>
            <Typography variant="body2" color="textSecondary">
              Day-wise status for every student across the month, with a running total
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={includeNative} onChange={(e) => setIncludeNative(e.target.checked)} />}
              label="Include at Native"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
                {MONTHS.map((m, i) => (
                  <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select label="Year" value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Download />} onClick={handleExport}>
              Export to Excel
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, px: 0.75, py: 1 }}><strong>Sl No</strong></TableCell>
                <TableCell sx={{ width: 110, maxWidth: 110, px: 0.75, py: 1 }}><strong>Name</strong></TableCell>
                {Array.from({ length: daysInMonth }, (_, i) => (
                  <TableCell key={i + 1} align="center" sx={{ px: 0.25, py: 1, width: 34, minWidth: 34 }}>{i + 1}</TableCell>
                ))}
                <TableCell align="center" sx={{ width: 90, px: 0.5, py: 1, fontSize: 11 }}><strong>Sadhana Total</strong></TableCell>
                <TableCell align="center" sx={{ width: 90, px: 0.5, py: 1, fontSize: 11 }}><strong>Japa Total</strong></TableCell>
                <TableCell align="center" sx={{ width: 90, px: 0.5, py: 1, fontSize: 11 }}><strong>SP Total</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={daysInMonth + 5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No students yet.
                  </TableCell>
                </TableRow>
              )}
              {data.map((row, idx) => {
                const numerator = row.p_count + (includeNative ? row.n_count : 0);
                const fraction = `${numerator}/${daysInMonth}`;
                // Reacts to Include at Native automatically - numerator
                // above already includes n_count when the toggle is on, and
                // this percentage is derived from that same numerator.
                const pct = daysInMonth > 0 ? Math.round((numerator / daysInMonth) * 100) : 0;
                const [spNumerator, spDenominator] = row.sp_total.split('/').map(Number);
                const spPct = spDenominator > 0 ? Math.round((spNumerator / spDenominator) * 100) : 0;
                return (
                  <TableRow key={row.student_id}>
                    <TableCell sx={{ px: 0.75, py: 1.1 }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, px: 0.75, py: 1.1, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.name}>{row.name}</TableCell>
                    {row.days.map((status, dayIdx) => (
                      <TableCell key={dayIdx} align="center" sx={{ px: 0.25, py: 1.1, fontWeight: 700, fontSize: 12, ...STATUS_STYLE[status] }}>
                        {status === 'U' ? '' : status}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 700, px: 0.5, py: 1.1, whiteSpace: 'nowrap' }}>{fraction} ({pct}%)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, px: 0.5, py: 1.1, whiteSpace: 'nowrap' }}>{fraction} ({pct}%)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, px: 0.5, py: 1.1, whiteSpace: 'nowrap' }}>{row.sp_total} ({spPct}%)</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          {[
            ['P', 'Present'],
            ['A', 'Absent'],
            ['N', 'Went to Native (counts as Present)'],
            ['NC', 'Class Not Conducted (excluded from total)'],
            ['U', 'Not yet marked'],
          ].map(([key, label]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, ...STATUS_STYLE[key] }} />
              <Typography variant="caption" color="textSecondary">{label}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
          <strong>Sadhana Total</strong> and <strong>Japa Total</strong> are both Present days / Days in the month —
          toggle <strong>Include at Native</strong> to add Went to Native days into the numerator too. <strong>SP
          Total</strong> is Weekly Special Class attendance for the month — Yes-count out of how many times the
          configured weekday(s) fall in this month.
        </Typography>
      </Container>
    </Box>
  );
}

export default SadhanaReport;
